import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import {
  useCreateDeviceModelMasterMutation,
  useDeleteDeviceModelMasterMutation,
  useGetDeviceModelsQuery,
  useGetDeviceModelMasterListQuery,
  useUpdateDeviceModelMasterMutation,
} from "../../redux/admin/deviceApi";
import { normalizeRole, ROLES } from "../../utils/rbacHelper";

const pink = "#f43f6b";

const tableCellSx = {
  borderBottom: "0",
  py: 1.1,
  fontSize: 13,
  color: "#252525",
  whiteSpace: "nowrap",
  verticalAlign: "top",
};

const initialForm = {
  deviceModelName: "",
  protocol: "",
  configurationCommand: "",
  locationCommand: "",
  immobilizerCommand: "",
  resetCommand: "",
  resumeCommand: "",
  timezone: "",
  apn: "",
  port: "",
  smsIp: "",
  smsApn: "",
  smsTimezone: "",
  manufacturer: "",
  immobilizerType: "",
};

const normalizeText = (value) => String(value || "").trim();

function ModelMasterFormDialog({
  open,
  mode,
  form,
  error,
  saving,
  readOnly = false,
  onClose,
  onChange,
  onSubmit,
  protocols = [],
}) {
  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            px: 3,
            py: 2.25,
            borderBottom: "1px solid #eceff3",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#202124" }}>
            {readOnly
              ? "View Device Model"
              : mode === "edit"
                ? "Edit Device Model"
                : "Add Device Model"}
          </Typography>
          <IconButton onClick={onClose} disabled={saving} sx={{ color: "#111", p: 0.5 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, py: 3 }}>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2.25,
            }}
          >
            <TextField
              label="Device Model Name *"
              value={form.deviceModelName}
              disabled={readOnly}
              onChange={(event) => onChange("deviceModelName", event.target.value)}
              fullWidth
              variant="standard"
            />
            <FormControl variant="standard" fullWidth required>
              <InputLabel id="model-master-protocol-label">Assign to Protocol</InputLabel>
              <Select
                labelId="model-master-protocol-label"
                value={form.protocol}
                disabled={readOnly}
                onChange={(event) => onChange("protocol", event.target.value)}
                label="Assign to Protocol"
              >
                {protocols.map((protocol) => (
                  <MenuItem key={protocol.key} value={protocol.key}>
                    {protocol.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Configuration Command *"
              value={form.configurationCommand}
              disabled={readOnly}
              onChange={(event) => onChange("configurationCommand", event.target.value)}
              fullWidth
              variant="standard"
            />
            <TextField
              label="Location Command"
              value={form.locationCommand}
              disabled={readOnly}
              onChange={(event) => onChange("locationCommand", event.target.value)}
              fullWidth
              variant="standard"
            />
            <TextField
              label="Immobilizer Command *"
              value={form.immobilizerCommand}
              disabled={readOnly}
              onChange={(event) => onChange("immobilizerCommand", event.target.value)}
              fullWidth
              variant="standard"
            />
            <TextField
              label="Reset Command *"
              value={form.resetCommand}
              disabled={readOnly}
              onChange={(event) => onChange("resetCommand", event.target.value)}
              fullWidth
              variant="standard"
            />
            <TextField
              label="Resume Command *"
              value={form.resumeCommand}
              disabled={readOnly}
              onChange={(event) => onChange("resumeCommand", event.target.value)}
              fullWidth
              variant="standard"
            />
            <TextField
              label="Timezone"
              value={form.timezone}
              disabled={readOnly}
              onChange={(event) => onChange("timezone", event.target.value)}
              fullWidth
              variant="standard"
            />
            <TextField
              label="APN"
              value={form.apn}
              disabled={readOnly}
              onChange={(event) => onChange("apn", event.target.value)}
              fullWidth
              variant="standard"
            />
            <TextField
              label="Port"
              value={form.port}
              disabled={readOnly}
              onChange={(event) => onChange("port", event.target.value)}
              fullWidth
              variant="standard"
            />
            <TextField
              label="SMS IP"
              value={form.smsIp}
              disabled={readOnly}
              onChange={(event) => onChange("smsIp", event.target.value)}
              fullWidth
              variant="standard"
            />
            <TextField
              label="SMS APN"
              value={form.smsApn}
              disabled={readOnly}
              onChange={(event) => onChange("smsApn", event.target.value)}
              fullWidth
              variant="standard"
            />
            <TextField
              label="SMS Timezone"
              value={form.smsTimezone}
              disabled={readOnly}
              onChange={(event) => onChange("smsTimezone", event.target.value)}
              fullWidth
              variant="standard"
            />
            <TextField
              label="Manufacturer"
              value={form.manufacturer}
              disabled={readOnly}
              onChange={(event) => onChange("manufacturer", event.target.value)}
              fullWidth
              variant="standard"
            />
            <TextField
              label="Immobilizer Type"
              value={form.immobilizerType}
              disabled={readOnly}
              onChange={(event) => onChange("immobilizerType", event.target.value)}
              fullWidth
              variant="standard"
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          variant="outlined"
          sx={{ minWidth: 120 }}
        >
          Cancel
        </Button>
        {!readOnly ? (
          <Button
            onClick={onSubmit}
            disabled={saving}
            variant="contained"
            sx={{ minWidth: 160 }}
          >
            {saving ? "Saving..." : mode === "edit" ? "Update Device Model" : "Add Device Model"}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

export default function DeviceModelModal({ open = true, onClose }) {
  const currentUser = useSelector((state) => state.auth.user);
  const currentUserRole = normalizeRole(currentUser?.role);
  const canManageModelMaster = [
    ROLES.SUPER_ADMIN,
    ROLES.MANAGER,
    ROLES.OFFICE_EMPLOYEE,
  ].includes(currentUserRole);
  const canReadOnlyModelMaster = [
    ROLES.SUB_ADMIN,
    ROLES.DEALER,
    ROLES.USER,
  ].includes(currentUserRole);
  const canAccessModelMaster = canManageModelMaster || canReadOnlyModelMaster;

  const [search, setSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [selectedId, setSelectedId] = useState("");
  const [form, setForm] = useState(initialForm);
  const [formError, setFormError] = useState("");
  const [deleteError, setDeleteError] = useState("");

  const {
    data: rows = [],
    isFetching,
    refetch,
  } = useGetDeviceModelMasterListQuery(undefined, { skip: !open });
  const [createModel, { isLoading: isCreating }] = useCreateDeviceModelMasterMutation();
  const [updateModel, { isLoading: isUpdating }] = useUpdateDeviceModelMasterMutation();
  const [deleteModel, { isLoading: isDeleting }] = useDeleteDeviceModelMasterMutation();
  const { data: deviceModelOptions = {} } = useGetDeviceModelsQuery();
  const protocols = Array.isArray(deviceModelOptions?.protocols)
    ? deviceModelOptions.protocols
    : [];

  const saving = isCreating || isUpdating;

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) =>
      [
        row.deviceModelName,
        row.protocol,
        row.configurationCommand,
        row.locationCommand,
        row.immobilizerCommand,
        row.resumeCommand,
        row.resetCommand,
        row.port,
        row.smsTimezone,
        row.manufacturer,
        row.immobilizerType,
      ].some((value) => String(value || "").toLowerCase().includes(query))
    );
  }, [rows, search]);

  const totalRows = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalRows);
  const visibleRows = filteredRows.slice(startIndex, endIndex);

  useEffect(() => {
    if (page !== safePage) {
      setPage(safePage);
    }
  }, [page, safePage]);

  const openCreateDialog = () => {
    if (!canManageModelMaster) return;
    setFormMode("create");
    setSelectedId("");
    setForm(initialForm);
    setFormError("");
    setFormOpen(true);
  };

  const openEditDialog = (row) => {
    if (!canManageModelMaster) return;
    setFormMode("edit");
    setSelectedId(row._id);
    setForm({
      deviceModelName: normalizeText(row.deviceModelName),
      protocol: normalizeText(row.protocol),
      configurationCommand: normalizeText(row.configurationCommand),
      locationCommand: normalizeText(row.locationCommand),
      immobilizerCommand: normalizeText(row.immobilizerCommand),
      resetCommand: normalizeText(row.resetCommand),
      resumeCommand: normalizeText(row.resumeCommand),
      timezone: normalizeText(row.timezone),
      apn: normalizeText(row.apn),
      port: normalizeText(row.port),
      smsIp: normalizeText(row.smsIp),
      smsApn: normalizeText(row.smsApn),
      smsTimezone: normalizeText(row.smsTimezone),
      manufacturer: normalizeText(row.manufacturer),
      immobilizerType: normalizeText(row.immobilizerType),
    });
    setFormError("");
    setFormOpen(true);
  };

  const openViewDialog = (row) => {
    setFormMode("view");
    setSelectedId(row._id);
    setForm({
      deviceModelName: normalizeText(row.deviceModelName),
      protocol: normalizeText(row.protocol),
      configurationCommand: normalizeText(row.configurationCommand),
      locationCommand: normalizeText(row.locationCommand),
      immobilizerCommand: normalizeText(row.immobilizerCommand),
      resetCommand: normalizeText(row.resetCommand),
      resumeCommand: normalizeText(row.resumeCommand),
      timezone: normalizeText(row.timezone),
      apn: normalizeText(row.apn),
      port: normalizeText(row.port),
      smsIp: normalizeText(row.smsIp),
      smsApn: normalizeText(row.smsApn),
      smsTimezone: normalizeText(row.smsTimezone),
      manufacturer: normalizeText(row.manufacturer),
      immobilizerType: normalizeText(row.immobilizerType),
    });
    setFormError("");
    setFormOpen(true);
  };

  const closeFormDialog = () => {
    if (saving) return;
    setFormOpen(false);
    setFormError("");
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!canManageModelMaster) return;
    const requiredFields = [
      "deviceModelName",
      "protocol",
      "configurationCommand",
      "immobilizerCommand",
      "resetCommand",
      "resumeCommand",
    ];
    const missingField = requiredFields.find((field) => !normalizeText(form[field]));
    if (missingField) {
      setFormError("Please fill all required fields.");
      return;
    }

    try {
      setFormError("");
      if (formMode === "edit" && selectedId) {
        await updateModel({ id: selectedId, ...form }).unwrap();
      } else {
        await createModel(form).unwrap();
      }
      setFormOpen(false);
      setForm(initialForm);
      setSelectedId("");
      refetch();
    } catch (error) {
      setFormError(
        error?.data?.message || "Unable to save device model right now."
      );
    }
  };

  const handleDelete = async (id) => {
    if (!canManageModelMaster) return;
    const confirmed = window.confirm("Delete this device model?");
    if (!confirmed) return;
    try {
      setDeleteError("");
      await deleteModel(id).unwrap();
      refetch();
    } catch (error) {
      setDeleteError(
        error?.data?.message || "Unable to delete device model right now."
      );
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "calc(100vw - 56px)",
            maxWidth: "1320px",
            height: "auto",
            maxHeight: "calc(100vh - 72px)",
            borderRadius: 0,
            m: 0,
            overflow: "hidden",
            bgcolor: "#fff",
            boxShadow: "0 16px 40px rgba(0,0,0,0.22)",
          },
        }}
      >
        <DialogContent sx={{ p: 0, height: "100%", display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              height: 56,
              px: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#202124" }}>
              Device Model
            </Typography>
            <IconButton onClick={onClose} sx={{ color: "#111", p: 0.5 }}>
              <CloseIcon sx={{ fontSize: 24 }} />
            </IconButton>
          </Box>

          <Box sx={{ bgcolor: "#f8f9fb", p: 1.5, pb: 1.5, flex: 1, minHeight: 0 }}>
            <Box sx={{ bgcolor: "#fff", boxShadow: "0 1px 10px rgba(0,0,0,0.03)" }}>
              <Box
                sx={{
                  minHeight: 56,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  px: 1.5,
                  py: 1,
                }}
              >
                <TextField
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setPage(1);
                  }}
                  placeholder="Search"
                  variant="standard"
                  InputProps={{
                    disableUnderline: true,
                    endAdornment: <SearchIcon sx={{ color: pink, fontSize: 21 }} />,
                  }}
                  sx={{
                    width: 260,
                    bgcolor: "#fff",
                    px: 1.6,
                    py: 1,
                    "& input": {
                      fontSize: 13,
                      color: "#111",
                      fontWeight: 500,
                    },
                  }}
                />

                <Stack direction="row" spacing={1.25} alignItems="center">
                  {canManageModelMaster ? (
                    <IconButton
                      onClick={openCreateDialog}
                      sx={{
                        color: "#2563eb",
                        border: "1px solid #dbe3f1",
                        borderRadius: 1.5,
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  ) : null}
                  <IconButton onClick={refetch} sx={{ color: "#7b7b7b", p: 0.4 }}>
                    <RefreshIcon sx={{ fontSize: 27 }} />
                  </IconButton>
                </Stack>
              </Box>

              {deleteError ? (
                <Alert severity="error" sx={{ mx: 1.5, mb: 1.5 }}>
                  {deleteError}
                </Alert>
              ) : null}

              {!canAccessModelMaster ? (
                <Alert severity="warning" sx={{ mx: 1.5, mb: 1.5 }}>
                  You do not have access to Device Model Master.
                </Alert>
              ) : null}

              <Box
                sx={{
                  height: 64,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1.5,
                  borderBottom: "1px solid #8c8c8c",
                }}
              >
                <FormControl size="small">
                  <Select
                    value={rowsPerPage}
                    onChange={(event) => {
                      setRowsPerPage(Number(event.target.value));
                      setPage(1);
                    }}
                    sx={{
                      width: 71,
                      height: 31,
                      fontSize: 13,
                      borderRadius: "4px",
                      bgcolor: "#fff",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.24)",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#111" },
                      "& .MuiSelect-icon": { color: pink, fontSize: 22 },
                    }}
                  >
                    {[10, 25, 50, 100].map((size) => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Typography sx={{ fontSize: 13, color: "#6b7280", pr: 1 }}>
                  {isFetching ? "Refreshing..." : `${totalRows} models`}
                </Typography>
              </Box>

              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  height: 420,
                  maxHeight: 420,
                  borderRadius: 0,
                  overflowY: "auto",
                  "&::-webkit-scrollbar": { width: 10 },
                  "&::-webkit-scrollbar-track": { bgcolor: "#eeeeee" },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: "#4f46e5",
                    borderRadius: 10,
                    "&:hover": { bgcolor: "#4338ca" },
                  },
                }}
              >
                <Table stickyHeader size="small" sx={{ minWidth: 1560 }}>
                  <TableHead>
                    <TableRow>
                      {[
                        { label: "Device Model", width: "14%" },
                        { label: "Assigned Protocol", width: "12%" },
                        { label: "Configuration Command", width: "18%" },
                        { label: "Location Command", width: "12%" },
                        { label: "Immobilizer Command", width: "12%" },
                        { label: "Resume Command", width: "12%" },
                        { label: "Reset Command", width: "10%" },
                        { label: "Immobilizer Type", width: "8%" },
                        { label: "Port", width: "6%" },
                        { label: "SMS Timezone", width: "8%" },
                        { label: "Action", width: "8%" },
                      ].map((head) => (
                        <TableCell
                          key={head.label}
                          sx={{
                            width: head.width,
                            bgcolor: "#f5f6f8",
                            color: "#777",
                            fontSize: 13,
                            fontWeight: 500,
                            borderBottom: "0",
                            py: 1.1,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {head.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleRows.length ? (
                      visibleRows.map((row) => (
                        <TableRow key={row._id}>
                          <TableCell sx={{ ...tableCellSx, fontWeight: 700 }}>
                            {row.deviceModelName || "-"}
                          </TableCell>
                          <TableCell sx={tableCellSx}>{row.protocol || "-"}</TableCell>
                          <TableCell sx={tableCellSx}>{row.configurationCommand || "-"}</TableCell>
                          <TableCell sx={tableCellSx}>{row.locationCommand || "-"}</TableCell>
                          <TableCell sx={tableCellSx}>{row.immobilizerCommand || "-"}</TableCell>
                          <TableCell sx={tableCellSx}>{row.resumeCommand || "-"}</TableCell>
                          <TableCell sx={tableCellSx}>{row.resetCommand || "-"}</TableCell>
                          <TableCell sx={tableCellSx}>{row.immobilizerType || "-"}</TableCell>
                          <TableCell sx={tableCellSx}>{row.port || "-"}</TableCell>
                          <TableCell sx={tableCellSx}>{row.smsTimezone || "-"}</TableCell>
                          <TableCell sx={tableCellSx}>
                            {canManageModelMaster ? (
                              <Stack direction="row" spacing={0.25}>
                                <IconButton
                                  onClick={() => openEditDialog(row)}
                                  sx={{ color: "#16a34a", p: 0.4 }}
                                >
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  onClick={() => handleDelete(row._id)}
                                  disabled={isDeleting}
                                  sx={{ color: "#dc2626", p: 0.4 }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            ) : canReadOnlyModelMaster ? (
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => openViewDialog(row)}
                                sx={{ minWidth: 0, px: 0.5, fontWeight: 700 }}
                              >
                                View
                              </Button>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={11} sx={{ py: 6, textAlign: "center", color: "#6b7280" }}>
                          {isFetching ? "Loading device models..." : "No device models found."}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box
                sx={{
                  minHeight: 47,
                  px: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderTop: "1px solid #e5e7eb",
                }}
              >
                <Typography sx={{ fontSize: 16, color: "#3b3b3b" }}>
                  Showing {totalRows ? startIndex + 1 : 0} to {endIndex} of {totalRows} entries
                </Typography>

                <Pagination
                  page={safePage}
                  count={totalPages}
                  onChange={(_, value) => setPage(value)}
                  siblingCount={1}
                  boundaryCount={1}
                  showFirstButton={false}
                  showLastButton={false}
                  shape="rounded"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      height: 31,
                      minWidth: 32,
                      borderRadius: 0,
                      border: "1px solid #7b7b7b",
                      color: "#111",
                      fontSize: 16,
                      m: 0,
                      bgcolor: "#fff",
                    },
                    "& .Mui-selected": {
                      bgcolor: "#141927 !important",
                      color: "#fff",
                      borderColor: "#141927",
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <ModelMasterFormDialog
        open={formOpen}
        mode={formMode}
        form={form}
        error={formError}
        saving={saving}
        readOnly={!canManageModelMaster || formMode === "view"}
        onClose={closeFormDialog}
        onChange={handleFormChange}
        onSubmit={handleSubmit}
        protocols={protocols}
      />
    </>
  );
}
